import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LottieSpinner } from '@/components/LottieSpinner/LottieSpinner'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import useMobileMediaQuery from '@/hooks/useMobileMediaQuery'
import { useListPaginatedOfTopicsQuery } from '@/store/api/RunReport/RunReport.query'
import type { listPaginatedOfTopicsList } from '@/store/api/RunReport/RunReport.types'
import { FOCUSED_TOPICS_MAX } from '@/utils/schemas'

interface SelectFocusedTopicsSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  value: string[]
  onSave: (next: string[]) => void
}

export const SelectFocusedTopicsSheet = ({
  open,
  onOpenChange,
  value,
  onSave,
}: SelectFocusedTopicsSheetProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const isMobile = useMobileMediaQuery()
  const { data, isLoading } = useListPaginatedOfTopicsQuery(
    { page: 1, pageSize: 100 },
    { skip: !open },
  )

  const [selected, setSelected] = useState<string[]>(value)

  useEffect(() => {
    if (open) setSelected(value)
  }, [open, value])

  const topics = data?.info.list ?? []

  const getLocalizedName = (topic: listPaginatedOfTopicsList) => {
    const translation = topic.translations?.find(
      (tr) => tr.language === language,
    )
    return translation?.name ?? topic.name
  }

  const toggleTopic = (topicName: string) => {
    setSelected((prev) => {
      if (prev.includes(topicName)) {
        return prev.filter((name) => name !== topicName)
      }
      if (prev.length >= FOCUSED_TOPICS_MAX) return prev
      return [...prev, topicName]
    })
  }

  const handleCancel = () => onOpenChange(false)
  const handleConfirm = () => {
    onSave(selected)
    onOpenChange(false)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? 'bottom' : 'right'}
        className="flex flex-col gap-4 max-md:h-[85vh] max-md:rounded-t-2xl sm:max-w-md"
      >
        <SheetHeader className="text-center sm:text-center">
          <SheetTitle className="flex items-center justify-center gap-3 pr-8">
            <span>
              {t(
                'dashboard.runReport.behaviorFlags.selectFocusedTopicsSheetTitle',
              )}
            </span>
            <span className="text-base font-normal text-neutralColor80">
              {selected.length}/{FOCUSED_TOPICS_MAX}
            </span>
          </SheetTitle>
          <SheetDescription>
            {t(
              'dashboard.runReport.behaviorFlags.selectFocusedTopicsSheetDescription',
              { max: FOCUSED_TOPICS_MAX },
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-1 min-h-0 flex-col gap-3 overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-1 items-center justify-center">
              <LottieSpinner width={48} height={48} />
            </div>
          ) : (
            topics.map((topic) => {
              const isSelected = selected.includes(topic.name)
              const isDisabled =
                !isSelected && selected.length >= FOCUSED_TOPICS_MAX
              return (
                <label
                  key={topic._id}
                  htmlFor={`topic-${topic._id}`}
                  className="flex cursor-pointer items-start justify-between gap-3 rounded-[10px] border border-neutralColor5 bg-baseColorHighlight p-3 transition-colors hover:bg-baseColor"
                >
                  <span className="flex-1 text-sm text-neutralColor">
                    {getLocalizedName(topic)}
                  </span>
                  <Checkbox
                    id={`topic-${topic._id}`}
                    checked={isSelected}
                    disabled={isDisabled}
                    onCheckedChange={() => toggleTopic(topic.name)}
                    className="mt-0.5"
                  />
                </label>
              )
            })
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={handleCancel}>
            {t('dashboard.runReport.behaviorFlags.cancel')}
          </Button>
          <Button type="button" onClick={handleConfirm}>
            {t('dashboard.runReport.behaviorFlags.confirm')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
